/**
 * 좌석 참여자(Comensal) 신원 훅 — 분할결제 (menu #814 / admin #2204)
 *
 * ## 토큰이 둘인 이유
 *
 * - `table_session_token` — **이 테이블**. QR 로 얻는다
 * - `comensal_device_token` — **그 안의 누구**. `scan` 으로 얻는다
 *
 * 세션 토큰만으로는 "누가" 를 알 수 없다. QR 사진 한 장이면 남의 테이블 세션 토큰을
 * 얻을 수 있으므로, 지분을 주고받는 연산은 사람 단위 토큰을 요구한다(admin #2212).
 *
 * ## 세션이 바뀌면 함께 무효다
 *
 * 좌석 토큰은 `TABLE_SESSION_RUNTIME_KEYS` 에 포함되어 `clearTableSession()` 이 함께
 * 지운다. 남아 있으면 **다른 테이블에서 남의 좌석 신원으로 요청**하게 된다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  splitPaymentApi,
  type Comensal,
} from '@/lib/api/split-payment';
import {
  clearComensalAccess,
  persistComensalAccess,
  setSplitPaymentAvailable,
} from '@/lib/comensalAccess';
import {
  bindTableSessionEntry,
  clearTableSessionEntry,
  promoteTableSessionEntry,
  refreshTableSessionEntryExpiry,
} from '@/lib/tableContext';
import {
  COMENSAL_ACCESS_CHANGED_EVENT,
  COMENSAL_DEVICE_TOKEN_KEY,
  SESSION_BILLS_CHANGED_EVENT,
  TABLE_SESSION_CHANGED_EVENT,
} from '@/lib/sessionStorageKeys';
import { logger } from '@/utils/logger';

export interface UseComensalSessionReturn {
  deviceToken: string | null;
  comensal: Comensal | null;
  isScanning: boolean;
  error: string | null;
  /** QR 스캔 — 아직 합류는 아니다(D2: Invitado 는 메뉴 열람만). */
  scan: (displayName?: string) => Promise<string | null>;
  /**
   * 합류 요청 — **코드 전략 매장 전용**(4자리 `code`).
   *
   * 대표 승인 전략에서는 부르지 않는다. 스캔 자체가 요청이고 대표가 승인한다.
   */
  join: (payload: { code: string }) => Promise<Comensal | null>;
  /** 저장된 토큰으로 현재 서버 권한을 다시 읽는다(새로고침/승인 polling). */
  refreshIdentity: () => Promise<Comensal | null>;
  /**
   * 좌석 신원이 없으면 **세션당 한 번만** 확보한다.
   *
   * ★ 실패해도 자동 재시도하지 않는다. 재시도하면 effect 의존성이 바뀌며 다시 불려
   * **실패 → 재호출 → 실패**가 무한 반복되고 서버를 두드린다. 사용자가 다시 시도할
   * 길은 `scan` 을 직접 부르는 것이다.
   */
  ensureIdentity: () => Promise<string | null>;
}

function readStoredToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const token = window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY);

  return token !== null && token !== '' ? token : null;
}

export function useComensalSession(
  sessionToken: string | null,
  entryToken: string | null = null,
): UseComensalSessionReturn {
  const [deviceToken, setDeviceToken] = useState<string | null>(readStoredToken);
  const [comensal, setComensal] = useState<Comensal | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accessToken = sessionToken ?? entryToken;
  const isEntryAccess = sessionToken === null && entryToken !== null;

  const persistServerIdentity = useCallback((identity: Comensal): void => {
    const liveToken = identity.session_token ?? sessionToken;
    if (identity.session_token !== undefined && isEntryAccess) {
      if (!promoteTableSessionEntry(identity.session_token)) {
        throw new Error('TABLE_SESSION_ENTRY_PROMOTION_FAILED');
      }
    }
    if (liveToken !== null) persistComensalAccess(identity, liveToken);
    if (typeof identity.session?.split_payment_available === 'boolean') {
      setSplitPaymentAvailable(identity.session.split_payment_available);
    }
  }, [sessionToken, isEntryAccess]);

  // 세션이 바뀌면 좌석 토큰도 무효다 — `clearTableSession()` 이 저장소에서 지우지만
  // 메모리 상태는 이 구독이 따라잡는다.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const sync = (): void => {
      const stored = readStoredToken();

      setDeviceToken(stored);

      if (stored === null) {
        setComensal(null);
        clearComensalAccess();
      }
    };

    window.addEventListener(TABLE_SESSION_CHANGED_EVENT, sync);
    window.addEventListener(COMENSAL_ACCESS_CHANGED_EVENT, sync);

    return () => {
      window.removeEventListener(TABLE_SESSION_CHANGED_EVENT, sync);
      window.removeEventListener(COMENSAL_ACCESS_CHANGED_EVENT, sync);
    };
  }, []);

  const scan = useCallback(
    async (displayName?: string): Promise<string | null> => {
      if (accessToken === null) {
        return null;
      }

      setIsScanning(true);
      setError(null);

      try {
        const scanned = isEntryAccess
          ? await splitPaymentApi.scanEntry(accessToken, displayName)
          : await splitPaymentApi.scan(accessToken, displayName);

        if (isEntryAccess) {
          if (
            scanned.entry_expires_at === undefined
            || !bindTableSessionEntry(
              accessToken,
              scanned.device_token,
              scanned.entry_expires_at,
            )
          ) {
            throw new Error('TABLE_SESSION_ENTRY_BIND_FAILED');
          }
        } else if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(COMENSAL_DEVICE_TOKEN_KEY, scanned.device_token);
        }

        setDeviceToken(scanned.device_token);
        setComensal(scanned);
        persistServerIdentity(scanned);

        return scanned.device_token;
      } catch (caught) {
        logger.error('[useComensalSession] scan 실패', caught);
        setError(caught instanceof Error ? caught.message : 'unknown');

        return null;
      } finally {
        setIsScanning(false);
      }
    },
    [accessToken, isEntryAccess, persistServerIdentity]
  );

  const refreshIdentity = useCallback(async (): Promise<Comensal | null> => {
    if (accessToken === null || deviceToken === null) {
      return null;
    }

    setIsScanning(true);
    setError(null);

    try {
      const current = isEntryAccess
        ? await splitPaymentApi.currentEntry(accessToken, deviceToken)
        : await splitPaymentApi.current(accessToken, deviceToken);
      if (
        isEntryAccess
        && (
          current.entry_expires_at === undefined
          || !refreshTableSessionEntryExpiry(accessToken, current.entry_expires_at)
        )
      ) {
        throw new Error('TABLE_SESSION_ENTRY_EXPIRY_REFRESH_FAILED');
      }
      setComensal(current);
      persistServerIdentity(current);
      if (
        typeof window !== 'undefined'
        && current.join_status === 'joined'
        && comensal?.join_status !== 'joined'
      ) {
        window.dispatchEvent(new Event(SESSION_BILLS_CHANGED_EVENT));
      }

      return current;
    } catch (caught) {
      logger.error('[useComensalSession] 신원 복구 실패', caught);
      setError(caught instanceof Error ? caught.message : 'unknown');

      const status = typeof caught === 'object' && caught !== null && 'status' in caught
        ? (caught as { status?: unknown }).status
        : null;
      if (status === 401 || status === 403 || status === 404) {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(COMENSAL_DEVICE_TOKEN_KEY);
        }
        setDeviceToken(null);
        setComensal(null);
        clearComensalAccess();
        if (isEntryAccess) clearTableSessionEntry();
      }

      return null;
    } finally {
      setIsScanning(false);
    }
  }, [accessToken, deviceToken, comensal?.join_status, isEntryAccess, persistServerIdentity]);

  const join = useCallback(
    async (payload: { code: string }): Promise<Comensal | null> => {
      if (accessToken === null || deviceToken === null) {
        return null;
      }

      setError(null);

      try {
        const response = isEntryAccess
          ? await splitPaymentApi.joinEntry(accessToken, deviceToken, payload)
          : await splitPaymentApi.join(accessToken, deviceToken, payload);
        if (
          isEntryAccess
          && (
            response.entry_expires_at === undefined
            || !refreshTableSessionEntryExpiry(accessToken, response.entry_expires_at)
          )
        ) {
          throw new Error('TABLE_SESSION_ENTRY_EXPIRY_REFRESH_FAILED');
        }
        // join 응답은 참여자 본문만 내려오므로 scan/current의 세션 메타를 잃지 않는다.
        const joined = response.session === undefined && comensal?.session !== undefined
          ? { ...response, session: comensal.session }
          : response;

        setComensal(joined);
        persistServerIdentity(joined);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(SESSION_BILLS_CHANGED_EVENT));
        }

        return joined;
      } catch (caught) {
        logger.error('[useComensalSession] join 실패', caught);
        setError(caught instanceof Error ? caught.message : 'unknown');

        return null;
      }
    },
    [accessToken, deviceToken, comensal?.session, isEntryAccess, persistServerIdentity]
  );

  // ★ 상태(`isScanning`)는 동기 락이 아니다 — StrictMode 이중 호출이나 같은 tick 의 두
  //   호출은 둘 다 갱신 전 값을 보고 통과한다. 시도 여부는 ref 로 즉시 잠근다.
  const scanAttemptedForRef = useRef<string | null>(null);
  const recoveryAttemptedForRef = useRef<string | null>(null);
  const identityInFlightRef = useRef<{
    key: string;
    request: Promise<string | null>;
  } | null>(null);

  const ensureIdentity = useCallback((): Promise<string | null> => {
    if (accessToken === null) {
      return Promise.resolve(null);
    }

    if (deviceToken !== null && comensal !== null) {
      return Promise.resolve(deviceToken);
    }

    const key = `${isEntryAccess ? 'entry' : 'session'}:${accessToken}:${deviceToken ?? 'new'}`;
    if (identityInFlightRef.current?.key === key) {
      return identityInFlightRef.current.request;
    }

    if (deviceToken !== null) {
      if (recoveryAttemptedForRef.current === key) {
        return Promise.resolve(null);
      }
      recoveryAttemptedForRef.current = key;
    } else {
      if (scanAttemptedForRef.current === accessToken) {
        return Promise.resolve(null);
      }
      scanAttemptedForRef.current = accessToken;
    }

    const request = (deviceToken !== null
      ? refreshIdentity().then((current) => current === null ? null : deviceToken)
      : scan()
    ).finally(() => {
      if (identityInFlightRef.current?.key === key) {
        identityInFlightRef.current = null;
      }
    });

    identityInFlightRef.current = { key, request };
    return request;
  }, [accessToken, deviceToken, comensal, isEntryAccess, refreshIdentity, scan]);

  return {
    deviceToken,
    comensal,
    isScanning,
    error,
    scan,
    join,
    refreshIdentity,
    ensureIdentity,
  };
}
